// WP-6.1 (M28 GL-1..8, S-31 `/goals`): goal + reserve queries/mutations over the
// engine-backed API, mirroring lib/rps.ts's exact hook shape.
import type { CurrencyCode, GoalStatus, ReserveStatus } from '@pfos/engine';
import {
  goalListResponseSchema,
  goalResponseSchema,
  reserveListResponseSchema,
  reserveRampUpStatusSchema,
  reserveResponseSchema,
  strictReserveViolationSchema,
  type GoalCreateRequest,
  type GoalEditRequest,
  type GoalResponse,
  type ReserveCreateRequest,
  type ReserveRampUpStatus,
  type ReserveResponse,
  type StrictReserveViolation,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const GOALS_KEY = ['goals'] as const;
export const RESERVES_KEY = ['reserves'] as const;

export function useGoals(filter: { status?: GoalStatus } = {}): UseQueryResult<GoalResponse[]> {
  const params = new URLSearchParams();
  if (filter.status !== undefined) params.set('status', filter.status);
  const qs = params.toString();
  return useQuery({
    queryKey: [...GOALS_KEY, filter],
    queryFn: async () => {
      const body = goalListResponseSchema.parse(
        await api.get(`/api/v1/goals${qs === '' ? '' : `?${qs}`}`),
      );
      return body.goals;
    },
  });
}

function useInvalidateGoals(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: GOALS_KEY });
  };
}

export function useCreateGoal(): UseMutationResult<GoalResponse, Error, GoalCreateRequest> {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async (input) => goalResponseSchema.parse(await api.post('/api/v1/goals', input)),
    onSuccess: invalidate,
  });
}

export function useEditGoal(id: string): UseMutationResult<GoalResponse, Error, GoalEditRequest> {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async (input) =>
      goalResponseSchema.parse(await api.patch(`/api/v1/goals/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function useCompleteGoal(id: string): UseMutationResult<GoalResponse, Error, void> {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async () =>
      goalResponseSchema.parse(await api.post(`/api/v1/goals/${id}/complete`)),
    onSuccess: invalidate,
  });
}

export function useArchiveGoal(id: string): UseMutationResult<GoalResponse, Error, void> {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async () => goalResponseSchema.parse(await api.post(`/api/v1/goals/${id}/archive`)),
    onSuccess: invalidate,
  });
}

export function useReserves(
  filter: { status?: ReserveStatus; goalId?: string } = {},
): UseQueryResult<ReserveResponse[]> {
  const params = new URLSearchParams();
  if (filter.status !== undefined) params.set('status', filter.status);
  if (filter.goalId !== undefined) params.set('goalId', filter.goalId);
  const qs = params.toString();
  return useQuery({
    queryKey: [...RESERVES_KEY, filter],
    queryFn: async () => {
      const body = reserveListResponseSchema.parse(
        await api.get(`/api/v1/reserves${qs === '' ? '' : `?${qs}`}`),
      );
      return body.reserves;
    },
  });
}

// A reserve mutation also changes its linked goal's derived allocatedMinor/remainingMinor
// (GL-1), so both keys are invalidated together — same cross-invalidation shape as
// lib/rps.ts's useInvalidateRps (settling an RP can create a transaction too).
function useInvalidateReserves(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: RESERVES_KEY }),
      queryClient.invalidateQueries({ queryKey: GOALS_KEY }),
    ]);
  };
}

export function useCreateReserve(): UseMutationResult<
  ReserveResponse,
  Error,
  ReserveCreateRequest
> {
  const invalidate = useInvalidateReserves();
  return useMutation({
    mutationFn: async (input) =>
      reserveResponseSchema.parse(await api.post('/api/v1/reserves', input)),
    onSuccess: invalidate,
  });
}

// GL-4: "releasable anytime ... all changes are logged" — a reason is required (enforced by
// ConfirmDialog's own requireReason before this ever gets called).
export function useReleaseReserve(id: string): UseMutationResult<ReserveResponse, Error, string> {
  const invalidate = useInvalidateReserves();
  return useMutation({
    mutationFn: async (reason: string) =>
      reserveResponseSchema.parse(await api.post(`/api/v1/reserves/${id}/release`, { reason })),
    onSuccess: invalidate,
  });
}

// GL-2 (LOCKED): also embedded verbatim in GET /api/v1/dashboard's own snapshot — this
// standalone query exists for callers that need it without a full dashboard fetch.
export function useStrictViolation(currency: CurrencyCode): UseQueryResult<StrictReserveViolation> {
  return useQuery({
    queryKey: ['goals-strict-violation', currency],
    queryFn: async () =>
      strictReserveViolationSchema.parse(
        await api.get(`/api/v1/goals/strict-violation?currency=${currency}`),
      ),
  });
}

// GL-6..8 ramp-up.
export function useRampUp(
  currency: CurrencyCode,
  asOf?: string,
): UseQueryResult<ReserveRampUpStatus> {
  const params = new URLSearchParams({ currency });
  if (asOf !== undefined) params.set('asOf', asOf);
  return useQuery({
    queryKey: ['goals-ramp-up', currency, asOf],
    queryFn: async () =>
      reserveRampUpStatusSchema.parse(await api.get(`/api/v1/goals/ramp-up?${params.toString()}`)),
  });
}
